// @flow

import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';

import type { Status } from 'types/Status';
import type { CompetitionInternationale } from 'types/competitionsInternationales';
import type { ArticleType } from 'types/Article';
import type { FullUserType } from 'types/User';
import { STATUS_SUCCESS } from 'constants/statusConstants';
import Loading from 'components/fragments/Loading';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import NewsListElement from 'components/newsletter/NewsListElement';
import { withHelmet } from 'infrastructure/seo/Helmet';
import { formatFullDate } from 'utils/dateUtils';

const DEFAULT_TAG = 'summer-series';

export type StateProps = {
  data: CompetitionInternationale,
  status: Status,
  articles: Array<ArticleType>,
  articlesStatus: Status,
  userPref: FullUserType
};

export type DispatchProps = {
  fetchArticle: (slug: string) => void
};

type Props = {
  match: any,
  history: any,
  location: any
} & StateProps &
  DispatchProps;

function getSlug(data: CompetitionInternationale) {
  if (data.meta.news_page_tag) {
    return data.meta.news_page_tag;
  }

  const newsBloc = data.content.data.find(({ tagName }) => tagName === 'rencontres-itl-competition-news');
  if (!newsBloc) {
    return DEFAULT_TAG;
  }

  const slug = newsBloc.attributes.find(({ name }) => name === 'tagslug');
  if (!slug || !slug.value) {
    return DEFAULT_TAG;
  }

  return slug.value;
}

class CompetitionsInternationalesNews extends Component<Props> {
  componentDidMount() {
    if (this.props.status === STATUS_SUCCESS && this.props.data) {
      this.props.fetchArticle(getSlug(this.props.data));
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.status !== prevProps.status && this.props.status === STATUS_SUCCESS && this.props.data) {
      this.props.fetchArticle(getSlug(this.props.data));
    }
  }

  render() {
    const { articles, articlesStatus, status, userPref, data } = this.props;

    if (status !== STATUS_SUCCESS || articlesStatus !== STATUS_SUCCESS) {
      return <Loading isWhite backgroundColor={'#FFFFFF'} />;
    }

    const newsPrincipale = articles.length > 0 ? articles[0] : null;
    const newsTop = articles.length > 1 ? articles.slice(1, 4) : [];
    const newsBottom = articles.length > 4 ? articles.slice(4, 10) : [];
    return (
      <>
        <section className="section section-competition-news">
          {data.meta.news_page_title && <h2>{data.meta.news_page_title}</h2>}
          <div className="row">
            <div className="col col-lg-5on9">
              {newsPrincipale && (
                <a
                  title={`Lire la suite de l'article : ${newsPrincipale.title}`}
                  className="box card card--billboard is-small-desktop mb-4 mb-md-5 mb-lg-6"
                  href={newsPrincipale.link}
                >
                  <div
                    className="card__thumbnail img-blue"
                    data-background-type="medium_large"
                    style={{ backgroundImage: 'url(' + newsPrincipale.illustration + ')' }}
                  ></div>
                  <div className="card__body">
                    <p className="card__date ft-h6">{`${formatFullDate(new Date(newsPrincipale.date))}`}</p>
                    <h2 className="card__title">{newsPrincipale.title}</h2>
                  </div>
                  <div className="card__footer">
                    <span className="btn btn--cheer">
                      <i className="icon icon-chevron-right link-icon is-inline"></i>
                      <span>Lire la suite</span>
                    </span>
                  </div>
                  <div className="card__type">
                    <span className="label">{newsPrincipale.label && newsPrincipale.label.name}</span>
                  </div>
                </a>
              )}
            </div>
            {newsTop.length > 0 && (
              <div className="col col-lg-3on9">
                <div className="news-slider mb-6 mb-md-7">
                  <div className="news-slider__wrapper">
                    {newsTop.map(news => (
                      <a
                        title={`Lire la suite de l'article : ${news.title}`}
                        key={news.id}
                        role="button"
                        tabIndex="0"
                        className="news news--row-lg"
                        href={news.link}
                      >
                        <article>
                          <span className="border" style={{ display: 'none' }}></span>
                          <div
                            className="news__thumbnail img-blue"
                            data-background-type="thumbnail"
                            style={{ backgroundImage: 'url(' + news.illustration + ')' }}
                          ></div>
                          <div>
                            <span className="news__title ft-h4">{news.title}</span>
                            <p className="ft-h6">
                              <span className="news__cat ft-up">{news.label && news.label.name}</span>
                              <span className="news__date">{`${formatFullDate(new Date(news.date))}`}</span>
                            </p>
                          </div>
                        </article>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="row grid-news">
            {newsBottom.map(article => (
              <div className="col col-lg-3on9" key={article.id}>
                <NewsListElement
                  srcset={{}}
                  key={article.id}
                  url={`/actualites/${article.label.slug}/${article.slug}`}
                  img={article.illustration}
                  title={article.title}
                  date={article.date}
                  description={article.caption}
                  userPref={userPref}
                  tags={article.tags}
                  excerpt={article.excerpt !== '' ? article.excerpt : article.meta_description}
                  readingTime={article.readingTime}
                />
              </div>
            ))}
          </div>
        </section>

        <div className="mt-7">
          {!!data &&
            !!data.content_news_tab &&
            !!data.content_news_tab.data &&
            !!data.content_news_tab.data.length &&
            data.content_news_tab.data.map((bloc, index) => <ContentBloc key={index} {...bloc} />)}
        </div>
      </>
    );
  }
}

export default withHelmet(withRouter(CompetitionsInternationalesNews))((Helmet, pageProps: Props) => {
  const { data, status } = pageProps;

  if (status !== STATUS_SUCCESS || !data) {
    null;
  }

  let meta_title = `Dernières Actualités`;
  if (data.title.raw.match(/Coupe /) || data.title.raw.match(/coupe /)) {
    meta_title = `${meta_title} de la ${data.title.raw}`;
  } else if (data.title.raw.match(/Tournoi /) || data.title.raw.match(/tournoi /)) {
    meta_title = `${meta_title} du ${data.title.raw}`;
  } else {
    meta_title = `${meta_title} des ${data.title.raw}`;
  }
  meta_title = `${meta_title} - FFR`;

  return (
    <Helmet htmlAttributes={{ lang: 'fr' }}>
      {meta_title && <title>{meta_title}</title>}
      {meta_title && <meta property={'og:title'} content={meta_title} />}
    </Helmet>
  );
});
